import { Authenticator } from '@aws-amplify/ui-react'
import { Pto } from '@outcoreaix/pto'
import { Flex, Spin } from 'antd'
import { AppRoutes } from 'app/app-routes'
import { Amplify } from 'aws-amplify'
import { settings } from 'config/settings'
import { Suspense, lazy } from 'react'
import { RouterProvider, createBrowserRouter } from 'react-router-dom'

import {
  AnonymousRoute,
  AppLayout,
  DataRoute,
  PrivateRoute,
  SettingsLayout,
  ThemeProvider,
  UserAccessRoute
} from 'components'

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: settings.aws.cognito.userPoolId,
      userPoolClientId: settings.aws.cognito.userPoolClientId,
      loginWith: {
        email: true,
        oauth: {
          domain: settings.aws.cognito.userPoolEndpoint,
          scopes: ['openid', 'email', 'phone'],
          responseType: 'code',
          redirectSignIn: [window.location.origin + '/login'],
          // redirectSignIn: [settings.aws.cognito.redirectUrl],
          redirectSignOut: [settings.aws.cognito.redirectUrl]
        }
      }
    }
  }
})

const Login = lazy(() => import('features/authorization/pages/Login'))
const OneTimeLink = lazy(() => import('features/authorization/pages/OneTimeLink'))
const OneTimeLinkAuth = lazy(() => import('features/authorization/pages/OneTimeLinkAuth'))
const AlertList = lazy(() => import('features/alerts/pages/AlertList'))
const InterviewsPage = lazy(() => import('features/interviews/pages/InterviewsPage'))
const RespondentInterviewsPage = lazy(() => import('features/interviews/pages/RespondentInterviewsPage'))
const AppSettings = lazy(() => import('features/app-settings/pages/AppSettings'))
const Workspaces = lazy(() => import('features/workspaces/pages/Workspaces'))
const WorkspaceSetup = lazy(() => import('features/workspaces/pages/WorkspaceSetup'))
const ProfileSettings = lazy(() => import('features/users/pages/ProfileSettings'))
const Support = lazy(() => import('features/app-settings/pages/Support'))
const UserInvite = lazy(() => import('features/users/pages/UserInvite'))
const UserFeedbacks = lazy(() => import('features/users/pages/UserFeedbacks'))
const Main = lazy(() => import('features/users/pages/Main/Main'))

const CpdNotice = lazy(() => import('features/learn-worlds/pages/CpdNotice'))
const SpaceSetup = lazy(() => import('features/spaces/pages/SpaceSetup'))
const SpaceGeneralSettings = lazy(() => import('features/spaces/pages/SpaceGeneralSettings'))
const SpaceAIConfig = lazy(() => import('features/spaces/pages/SpaceAIConfig'))
const SpaceOnboardingInterview = lazy(() => import('features/spaces/pages/SpaceOnboardingInterview'))
const SpaceMembers = lazy(() => import('features/spaces/pages/SpaceMembers'))
const CustomEmailsHistory = lazy(() => import('features/spaces/pages/CustomEmailsHistory/CustomEmailsHistory'))
const SpaceMemberProfile = lazy(() => import('features/spaces/pages/SpaceMemberProfile'))
const ConversationStartersManagement = lazy(
  () => import('features/conversationStartersManagement/pages/conversationStartersManagement')
)
const SystemAdminDashboard = lazy(() => import('features/systemAdminDashboard/pages/systemAdminDashboard'))
const DbDocsViewer = lazy(() => import('features/systemAdminDashboard/pages/DbDocsViewer'))
const OffboardingHistory = lazy(() => import('features/systemAdminDashboard/pages/OffboardingHistory'))
const ChangelogManagement = lazy(() => import('features/systemAdminDashboard/pages/ChangelogManagement'))
const ChangelogUserReadsAudit = lazy(() => import('features/systemAdminDashboard/pages/ChangelogUserReadsAudit'))
const EmailTemplates = lazy(() => import('features/emailTemplates/pages/EmailTemplates'))
const ToolManagement = lazy(() => import('features/ai-tools/pages/ToolManagement'))
const ProductManagement = lazy(() => import('features/productManagement/pages/productManagement'))
const ProductSetUp = lazy(() => import('features/productManagement/pages/productSetUp'))
const SystemAdminSpaceManagement = lazy(
  () => import('features/systemAdminSpaceManagement/pages/systemAdminSpaceManagement')
)
const WorkspaceOffboarding = lazy(() => import('features/workspaces/pages/WorkspaceOffboarding'))
const Flows = lazy(() => import('features/app-flows/pages/Flows'))
const FlowInfo = lazy(() => import('features/app-flows/pages/FlowInfo'))
const InductionCategories = lazy(() => import('features/app-flows/pages/Categories'))

const WorkspaceGeneralSettings = lazy(() => import('features/workspaces/pages/WorkspaceGeneralSettings'))
const WorkspaceMembersSettings = lazy(() => import('features/workspaces/pages/WorkspaceMembersSettings'))

const LearnerOnboardingFlow = lazy(() => import('features/app-flows/pages/OnboardingFlow'))
const HomePage = lazy(() => import('features/spaces/pages/HomePage'))
const SpacePage = lazy(() => import('features/spaces/pages/Space'))
const ArtifactPage = lazy(() => import('features/artifacts/pages/ArtifactPage'))

const NotFound = lazy(() => import('not-found'))
const ErrorPage = lazy(() => import('error-page'))

const EmptyPage = lazy(() => import('empty-page'))

const Terms = lazy(() => import('features/users/pages/Terms'))
const PrivacyPolicy = lazy(() => import('features/users/pages/PrivacyPolicy'))
const Dashboard = lazy(() => import('features/dashboards/pages/Dashboard'))
const WhatsNew = lazy(() => import('features/changelog/pages/WhatsNew/WhatsNew'))

const router = createBrowserRouter([
  {
    errorElement: <ErrorPage />,
    element: <PrivateRoute />,
    children: [
      {
        element: <Main />,
        path: AppRoutes.main
      },
      {
        element: <UserInvite />,
        path: `${AppRoutes.userInvite}/:inviteToken`
      },
      {
        element: <DataRoute />,
        errorElement: <ErrorPage />,
        children: [
          {
            element: <UserAccessRoute roleRequired={[Pto.Workspaces.WorkspaceRole.Contact]} />,
            children: [
              {
                path: `${AppRoutes.workspaces}/:workspaceId/space/:spaceId${AppRoutes.learnerOnboarding}`,
                element: <LearnerOnboardingFlow />
              }
            ]
          },
          {
            element: (
              <UserAccessRoute
                roleRequired={[Pto.Workspaces.WorkspaceRole.Staff, Pto.Workspaces.WorkspaceRole.Admin]}
              />
            ),
            children: [
              {
                path: `${AppRoutes.workspaces}/:workspaceId${AppRoutes.cpdNotice}`,
                element: <CpdNotice />
              }
            ]
          },
          {
            element: <AppLayout hasSubMenu />,
            errorElement: <ErrorPage />,
            children: [
              {
                element: (
                  <UserAccessRoute
                    roleRequired={[
                      Pto.Workspaces.WorkspaceRole.Contact,
                      Pto.Workspaces.WorkspaceRole.Staff,
                      Pto.Workspaces.WorkspaceRole.Admin
                    ]}
                  />
                ),
                children: [
                  {
                    path: `${AppRoutes.workspaces}/:workspaceId/space/:spaceId`,
                    element: <SpacePage />
                  },
                  {
                    path: `${AppRoutes.workspaces}/:workspaceId/space/:spaceId${AppRoutes.artifact}`,
                    element: <ArtifactPage />
                  },
                  {
                    path: `${AppRoutes.workspaces}/:workspaceId/space/`,
                    element: <EmptyPage />
                  },
                  {
                    path: `${AppRoutes.workspaces}/:workspaceId`,
                    element: <EmptyPage />
                  },
                  {
                    element: <SettingsLayout />,
                    children: [
                      {
                        path: `${AppRoutes.settings}/:workspaceId/profile-settings`,
                        element: <ProfileSettings />
                      },
                      {
                        path: `${AppRoutes.settings}/:workspaceId/support`,
                        element: <Support />
                      }
                    ]
                  }
                ]
              },
              {
                element: <UserAccessRoute roleRequired={[Pto.Workspaces.WorkspaceRole.Contact]} />,
                children: [
                  {
                    path: `${AppRoutes.workspaces}/:workspaceId/space/:spaceId/home`,
                    element: <HomePage />
                  }
                ]
              },
              {
                element: (
                  <UserAccessRoute
                    roleRequired={[Pto.Workspaces.WorkspaceRole.Admin, Pto.Workspaces.WorkspaceRole.Staff]}
                  />
                ),
                children: [
                  {
                    path: `${AppRoutes.workspaces}/:workspaceId/dashboard`,
                    element: <Dashboard />
                  },
                  {
                    path: `${AppRoutes.workspaces}/:workspaceId${AppRoutes.whatsNew}`,
                    element: <WhatsNew />
                  },
                  {
                    path: `${AppRoutes.workspaces}/:workspaceId${AppRoutes.alerts}`,
                    element: <AlertList />
                  },
                  {
                    path: `${AppRoutes.workspaces}/:workspaceId${AppRoutes.alerts}/:alertId`,
                    element: <AlertList />
                  },
                  {
                    path: `${AppRoutes.workspaces}/:workspaceId${AppRoutes.interviews}`,
                    element: <InterviewsPage />
                  },
                  {
                    path: `${AppRoutes.workspaces}/:workspaceId${AppRoutes.respondentInterviews}/:respondentUserId`,
                    element: <RespondentInterviewsPage />
                  },
                  {
                    element: <SettingsLayout />,
                    children: [
                      {
                        path: `/settings/:workspaceId`,
                        element: <EmptyPage />
                      },
                      {
                        path: `/settings/:workspaceId/general`,
                        element: <WorkspaceGeneralSettings />
                      },
                      {
                        path: `/settings/:workspaceId/space/:spaceId/general`,
                        element: <SpaceGeneralSettings />
                      },
                      {
                        path: `/settings/:workspaceId/space/:spaceId/ai-config`,
                        element: <SpaceAIConfig />
                      },
                      {
                        path: `/settings/:workspaceId/space/:spaceId/members`,
                        element: <SpaceMembers />
                      },
                      {
                        path: `/settings/:workspaceId/space/:spaceId/custom-emails-history`,
                        element: <CustomEmailsHistory />
                      },
                      {
                        path: `/settings/:workspaceId/space/:spaceId/members/:memberId`,
                        element: <SpaceMemberProfile />
                      },
                      {
                        path: `/settings/:workspaceId/space/:spaceId/users/:userId`,
                        element: <SpaceMemberProfile />
                      }
                    ]
                  }
                ]
              }
            ]
          },
          {
            element: <AppLayout />,
            errorElement: <ErrorPage />,
            children: [
              {
                element: <UserAccessRoute roleRequired={[Pto.Users.UserRole.SystemAdmin]} />,
                children: [
                  {
                    path: AppRoutes.workspaces,
                    element: <Workspaces />
                  },
                  {
                    path: `${AppRoutes.workspaces}/:workspaceId/members`,
                    element: <WorkspaceMembersSettings type="system" />
                  },
                  {
                    path: AppRoutes.feedbacks,
                    element: <UserFeedbacks />
                  },
                  {
                    path: AppRoutes.appSettings,
                    element: <AppSettings />
                  },
                  {
                    path: AppRoutes.conversationStartersManagement,
                    element: <ConversationStartersManagement />
                  },
                  {
                    path: AppRoutes.systemAdminDashboard,
                    element: <SystemAdminDashboard />
                  },
                  {
                    path: AppRoutes.dbDocsViewer,
                    element: <DbDocsViewer />
                  },
                  {
                    path: AppRoutes.offboardingHistory,
                    element: <OffboardingHistory />
                  },
                  {
                    path: AppRoutes.changelogManagement,
                    element: <ChangelogManagement />
                  },
                  {
                    path: AppRoutes.changelogUserReadsAudit,
                    element: <ChangelogUserReadsAudit />
                  },
                  {
                    path: AppRoutes.emailTemplates,
                    element: <EmailTemplates />
                  },
                  {
                    path: AppRoutes.toolManagement,
                    element: <ToolManagement />
                  },
                  {
                    path: AppRoutes.productManagement,
                    element: <ProductManagement />
                  },
                  {
                    path: AppRoutes.flows,
                    element: <Flows />
                  },
                  {
                    path: `${AppRoutes.flows}/:flowId`,
                    element: <FlowInfo />
                  },
                  {
                    path: `${AppRoutes.inductionCategories}`,
                    element: <InductionCategories />
                  },
                  {
                    path: `${AppRoutes.productManagement}/set-up`,
                    element: <ProductSetUp />
                  },
                  {
                    path: `${AppRoutes.productManagement}/:productId/set-up`,
                    element: <ProductSetUp />
                  },
                  {
                    path: `${AppRoutes.workspaces}/:workspaceId${AppRoutes.workspaceAllSpaces}`,
                    element: <SystemAdminSpaceManagement />
                  },
                  {
                    path: `${AppRoutes.workspaces}/:workspaceId${AppRoutes.workspaceOffboarding}`,
                    element: <WorkspaceOffboarding />
                  }
                ]
              }
            ]
          },
          {
            element: (
              <UserAccessRoute
                roleRequired={[Pto.Workspaces.WorkspaceRole.Admin, Pto.Workspaces.WorkspaceRole.Staff]}
              />
            ),
            children: [
              {
                path: `${AppRoutes.workspaces}/:workspaceId/set-up`,
                element: <WorkspaceSetup />
              },
              {
                path: `${AppRoutes.workspaces}/:workspaceId/space/set-up`,
                element: <SpaceSetup />
              },
              {
                path: `${AppRoutes.workspaces}/:workspaceId/space/:spaceId/set-up`,
                element: <SpaceSetup />
              }
            ]
          }
        ]
      }
    ]
  },
  {
    path: AppRoutes.login,
    element: (
      <AnonymousRoute>
        <Login />
      </AnonymousRoute>
    )
  },
  {
    path: AppRoutes.oneTimeLink,
    element: (
      <AnonymousRoute>
        <OneTimeLink />
      </AnonymousRoute>
    )
  },
  {
    path: `${AppRoutes.oneTimeToken}/:oneTimeToken`,
    element: <OneTimeLinkAuth />
  },
  {
    path: AppRoutes.terms,
    element: <Terms />
  },
  {
    path: AppRoutes.policy,
    element: <PrivacyPolicy />
  },
  {
    path: '*',
    element: <NotFound />
  }
])

function App() {
  return (
    <Authenticator.Provider>
      <ThemeProvider>
        <Suspense
          fallback={
            <Flex className="w-screen h-dvh lg:h-screen" justify="center" align="center">
              <Spin spinning={true} size="large" />
            </Flex>
          }
        >
          <RouterProvider
            router={router}
            fallbackElement={
              <Flex className="w-screen h-dvh lg:h-screen" justify="center" align="center">
                <Spin spinning={true} size="large" />
              </Flex>
            }
          />
        </Suspense>
      </ThemeProvider>
    </Authenticator.Provider>
  )
}

export default App
